import { arraysEqual } from './performance';

export const formatSecondaryProducts = (payload) => {
  const data = Object.entries(payload.data.product).reduce((obj, product) => {
    if (!product) return obj;
    const key = product[0] ?? false;
    const value = product[1]?.value ?? false;
    if (!key || !value) return obj;

    let parsedArray = JSON.parse(value.replace(/'/g, '"')) ?? [];
    const containsItems = parsedArray.length > 0;
    if (containsItems) {
      parsedArray = parsedArray.map((value) => {
        if (value === '') return false;
        if (value === 'true' || value === 'false') return JSON.parse(value);

        return value;
      });
    }

    obj[key] = parsedArray;

    return obj;
  }, {});

  return data;
};

export const formatID = (id) => {
  const matchType = ['ProductVariant/', 'Product/', 'Collection/'].find(
    (match) => id.includes(match)
  );
  return matchType ? parseInt(id.split(matchType)[1].trim()) : id;
};

export const parseTag = (tags, key) => {
  return tags
    .find((tag) => tag.includes(key))
    ?.split(':')[1]
    ?.trim();
};

/**
 * Builds a URL for a related product with the best matching in-stock variant.
 * Exact-matches current selections against the related product's variants.
 * Falls back to the first in-stock variant matching a single option (e.g. Size).
 * @param {Object} product - { handle, variants: [{ id, quantityAvailable, selectedOptions }] }
 * @param {Object[]} selectedOptions - [{ name, value }]
 * @returns {string} URL with variant param, or bare product URL
 */
export const relatedProductURL = (product, selectedOptions) => {
  const { handle, variants } = product;
  if (!variants?.length) return `/products/${handle}`;

  // Exact match on all selected options
  const exactMatch = variants.find((variant) =>
    arraysEqual(variant.selectedOptions, selectedOptions)
  );
  if (exactMatch)
    return `/products/${handle}?variant=${formatID(exactMatch.id)}`;

  // Fallback: prioritize retaining Size, then try remaining options
  const sizeSelection = selectedOptions.find(
    (selection) => selection.name === 'Size'
  );
  const remainingSelections = selectedOptions.filter(
    (selection) => selection.name !== 'Size'
  );
  const fallbackOrder = sizeSelection
    ? [sizeSelection, ...remainingSelections]
    : selectedOptions;

  for (const { name, value } of fallbackOrder) {
    const fallbackMatch = variants.find((variant) =>
      variant.selectedOptions.some(
        (option) => option.name === name && option.value === value
      )
    );
    if (fallbackMatch)
      return `/products/${handle}?variant=${formatID(fallbackMatch.id)}`;
  }

  return `/products/${handle}`;
};

/**
 * Converts a string to a URL-safe handle by lowercasing, replacing spaces with
 * hyphens, and stripping all remaining non-alphanumeric/hyphen characters.
 * @param {string} text - The input string to convert
 * @returns {string} The handle string
 */
export const toHandle = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '');
};

/**
 * Parses a Shopify rich text JSON tree into an HTML string.
 * Headings (h1–h6) render as <p> with class t1–t6 instead of <hN> tags.
 * @param {string|Object} text - JSON string or parsed rich text object
 * @returns {string} HTML string
 */
export const parseRichText = (text) => {
  if (!text) return '';

  let tree;
  try {
    tree = typeof text === 'string' ? JSON.parse(text) : text;
  } catch {
    return '';
  }
  if (!tree || !tree.children) return '';

  /**
   * Renders an inline text node with bold, italic, and link formatting.
   * @param {Object} node - A rich text node
   * @returns {string} HTML string for the node
   */
  const renderNode = (node) => {
    if (node.type === 'text') {
      let html = node.value ?? '';
      if (node.bold) html = `<strong>${html}</strong>`;
      if (node.italic) html = `<em>${html}</em>`;
      return html;
    }

    if (node.type === 'link') {
      const inner = (node.children || []).map(renderNode).join('');
      return `<a href="${node.url}">${inner}</a>`;
    }

    return '';
  };

  /**
   * Renders a block-level node (paragraph, heading, list, list-item).
   * @param {Object} block - A block-level rich text node
   * @returns {string} HTML string for the block
   */
  const renderBlock = (block) => {
    switch (block.type) {
      case 'heading': {
        const level = Math.min(Math.max(block.level || 1, 1), 6);
        const inner = (block.children || []).map(renderNode).join('');
        return `<p class="t${level}">${inner}</p>`;
      }

      case 'paragraph': {
        const inner = (block.children || []).map(renderNode).join('');
        return `<p>${inner}</p>`;
      }

      case 'list': {
        const tag = block.listType === 'ordered' ? 'ol' : 'ul';
        const items = (block.children || []).map(renderBlock).join('');
        return `<${tag}>${items}</${tag}>`;
      }

      case 'list-item': {
        const inner = (block.children || []).map(renderNode).join('');
        return `<li>${inner}</li>`;
      }

      default:
        return '';
    }
  };

  return tree.children.map(renderBlock).join('');
};
